import { Colors, Icon, Spinner } from '@amelio/ui-library';
import { useMemo, useState } from 'react';

import { SkillCategoryAccordion } from './SkillCategoryAccordion';

import type { EmployeeCompetency } from '@/types/manager-assessment/Competency';
import type { Skill } from '@/types/manager-assessment/Skill';

import { useAmelioTranslations } from '@/hooks';

interface SkillReviewerProps {
    selectedSkills: Skill[];
    employeeCompetencies: EmployeeCompetency[];
    allCompetencies: EmployeeCompetency[];
    onRemoveSkill: (skillId: string) => void;
    onAddSkills: () => void;
    isLoading: boolean;
}

export const SkillReviewer = ({
    selectedSkills,
    employeeCompetencies,
    allCompetencies,
    onRemoveSkill,
    onAddSkills,
    isLoading,
}: SkillReviewerProps) => {
    const { t } = useAmelioTranslations();
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

    const groupedSkills = useMemo(() => {
        const grouped = new Map<string, Skill[]>();
        selectedSkills.forEach((skill) => {
            const comp =
                employeeCompetencies.find((c) => c.id === skill.id) ??
                allCompetencies.find((c) => c.id === skill.id);
            const cat = comp?.groupName ?? t('managerAssessmentSetup.ungrouped');
            const existing = grouped.get(cat) ?? [];
            existing.push(skill);
            grouped.set(cat, existing);
        });
        return Array.from(grouped.entries());
    }, [selectedSkills, employeeCompetencies, allCompetencies, t]);

    const toggleCategory = (category: string) => {
        setExpandedCategories((prev) => {
            const next = new Set(prev);
            if (next.has(category)) {
                next.delete(category);
            } else {
                next.add(category);
            }
            return next;
        });
    };

    if (isLoading) {
        return (
            <div
                className={'flex items-center justify-center py-8'}
                data-testid={'skill-reviewer-loading'}
            >
                <Spinner size={'md'} />
            </div>
        );
    }

    return (
        <div
            className={'flex flex-col gap-4'}
            data-testid={'skill-reviewer'}
        >
            <div className={'flex w-full flex-col gap-4'}>
                {groupedSkills.map(([category, skills]) => (
                    <SkillCategoryAccordion
                        key={category}
                        category={category}
                        skills={skills}
                        isOpen={expandedCategories.has(category)}
                        onToggle={() => toggleCategory(category)}
                        onRemoveSkill={onRemoveSkill}
                    />
                ))}
            </div>

            <button
                type={'button'}
                onClick={onAddSkills}
                className={
                    'flex w-fit items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium text-white shadow-sm transition-all hover:opacity-90 hover:shadow-md'
                }
                style={{
                    backgroundImage: 'var(--gradient-primary-button)',
                    borderColor: 'var(--color-primary-800)',
                }}
                data-testid={'add-skills-button'}
            >
                <Icon
                    name={'essential-add'}
                    size={20}
                    color={Colors.WHITE}
                />

                {t('managerAssessmentSetup.addSkillsToAssess')}
            </button>

            {selectedSkills.length === 0 && (
                <p
                    className={'py-4 text-center text-sm text-gray-500'}
                    data-testid={'skill-reviewer-empty'}
                >
                    {t('managerAssessmentSetup.noCompetenciesFound')}
                </p>
            )}
        </div>
    );
};
